import { useEffect, useMemo, useState } from "react";

import startCase from "lodash/startCase";

import { Button, Input, Modal, Select, Text } from "@alpacahq/ui/old";

import type { Order } from "~/v2/api/rest/account";
import { CRYPTO_TIME_IN_FORCE_OPTIONS } from "~/v2/constants/cryptoOrder";
import { EQUITY_TIME_IN_FORCE_OPTIONS } from "~/v2/constants/equityOrder";
import { OPTION_TIME_IN_FORCE_OPTIONS } from "~/v2/constants/optionOrder";

// Replacing us_equity order has some TIF restrictions
const EQUITY_NOT_ALLOWED_TIME_IN_FORCE_VALUES = ["day-ext"];
// Replacing a us_option order only supports `day`. `gtc` is valid for option
// order entry but is rejected by the replace (PATCH) endpoint, which accepts
// `day` only for options.
const OPTION_NOT_ALLOWED_TIME_IN_FORCE_VALUES = ["gtc"];

type TimeInForceOption = {
  label: string;
  subLabel?: string;
  value: string;
  orderTypes: string[];
};

const ASSET_CLASS_ORDER_TYPE_MAP: Record<string, TimeInForceOption[]> = {
  us_equity: EQUITY_TIME_IN_FORCE_OPTIONS.filter(
    (timeInForce) =>
      !EQUITY_NOT_ALLOWED_TIME_IN_FORCE_VALUES.includes(timeInForce.value),
  ),
  us_option: OPTION_TIME_IN_FORCE_OPTIONS.filter(
    (timeInForce) =>
      !OPTION_NOT_ALLOWED_TIME_IN_FORCE_VALUES.includes(timeInForce.value),
  ),
  crypto: CRYPTO_TIME_IN_FORCE_OPTIONS,
};

type TimeInForceSelectOption = {
  label: string;
  value: string;
};

export const getReplaceTimeInForceOptions = (
  assetClass: string | undefined,
  type: string | undefined,
): TimeInForceSelectOption[] =>
  (
    ASSET_CLASS_ORDER_TYPE_MAP[assetClass ?? ""] ??
    ASSET_CLASS_ORDER_TYPE_MAP.us_equity
  )
    .filter((timeInForce) => timeInForce.orderTypes.includes(type ?? ""))
    .map(({ label, subLabel, value }) => ({
      label: subLabel ? `${label} - ${subLabel}` : label,
      value,
    }));

// Keep the order's current TIF if it's still selectable, otherwise fall back to
// the first offered value. This prevents an order's existing-but-now-disallowed
// TIF (e.g. a GTC option order, since option replacement only supports `day`)
// from silently being submitted to the replace endpoint when the user edits
// another field without touching the time-in-force dropdown.
export const getInitialReplaceTimeInForce = (
  currentTimeInForce: string | undefined,
  options: TimeInForceSelectOption[],
) =>
  options.some((option) => option.value === currentTimeInForce)
    ? currentTimeInForce
    : options[0]?.value;

export interface ReplaceOrderDetails {
  qty?: string;
  time_in_force?: string;
  limit_price?: string;
  stop_price?: string;
  trail?: string;
}

export interface ReplaceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (orderDetails: ReplaceOrderDetails) => void;
  order: Order;
}

export const ReplaceOrderModal = ({
  isOpen,
  onClose,
  onConfirm,
  order,
}: ReplaceOrderModalProps) => {
  const [state, setState] = useState<ReplaceOrderDetails>({});

  const timeInForceOptions = useMemo(
    () => getReplaceTimeInForceOptions(order.asset_class, order.type),
    [order.asset_class, order.type],
  );

  // init state with current order details
  useEffect(() => {
    setState({
      qty: order.qty ?? undefined,
      time_in_force: getInitialReplaceTimeInForce(
        order.time_in_force,
        timeInForceOptions,
      ),
      limit_price: order.limit_price ?? undefined,
      stop_price: order.stop_price ?? undefined,
      trail: order.trail_price ?? undefined,
    });
  }, [order, timeInForceOptions]);

  const numProps = {
    precision: 2,
    step: 0.01,
    min: 0.01,
    leftIcon: <span>$</span>,
  };

  const isContractLike = order.symbol.length > 12;

  return (
    <Modal
      title="Replace Order"
      isOpen={isOpen}
      onClose={onClose}
      controls={
        <div className="flex items-center justify-between gap-4">
          <Button type="button" variant="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button type="button" variant="red" onClick={() => onConfirm(state)}>
            Replace Order
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Text className="font-medium text-gray-900 text-lg">
            {order.symbol}
          </Text>
          <Text className="flex gap-2 text-gray-600">
            <span>{startCase(order.type)}</span>
            <span>·</span>
            <span>{startCase(order.side)}</span>
            <span>·</span>
            <span>
              {order.qty} {isContractLike ? "contract(s)" : "share(s)"}
            </span>
          </Text>
        </div>

        {!!order.limit_price && (
          <Input
            type="number"
            label="Limit Price"
            {...numProps}
            value={state.limit_price}
            onChange={(event) =>
              setState((prevState) => ({
                ...prevState,
                limit_price: event.target.value,
              }))
            }
            disabled={!order.limit_price}
          />
        )}

        <Input
          type="number"
          label="Quantity"
          value={state.qty}
          onChange={(event) =>
            setState((prevState) => ({ ...prevState, qty: event.target.value }))
          }
          min={1}
        />

        {!!order.trail_price && (
          <Input
            type="number"
            label="Trail"
            {...numProps}
            value={state.trail}
            onChange={(event) =>
              setState((prevState) => ({
                ...prevState,
                trail: event.target.value,
              }))
            }
            disabled={!order.trail_price}
          />
        )}

        {!!order.stop_price && (
          <Input
            type="number"
            label="Stop Price"
            {...numProps}
            value={state.stop_price}
            onChange={(event) =>
              setState((prevState) => ({
                ...prevState,
                stop_price: event.target.value,
              }))
            }
            disabled={!order.stop_price}
          />
        )}

        <div>
          <Text className="mb-2.5 block font-medium text-gray-900">
            Time In Force
          </Text>
          <Select
            multiple={false}
            placeholder="Time In Force"
            value={state.time_in_force}
            onChange={(time_in_force) =>
              setState((prevState) => ({ ...prevState, time_in_force }))
            }
            options={timeInForceOptions}
          />
        </div>
      </div>
    </Modal>
  );
};
